import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Loader2 } from 'lucide-react';
import {
  Alert,
  AlertDescription,
  Button,
  DialogFooter,
  Textarea,
} from '@roger/ui-components';
import { useToast } from '../../hooks/useToastContext';
import {
  completeEpisode,
  getEpisode,
  reopenEpisode,
  updateEpisode,
} from '../../services/api';
import type {
  EpisodeResponse,
  EpisodeUpdate,
} from '../../services/api/types.gen';
import { getBusinessErrorMessage } from '../../utils/apiErrorDetail';
import {
  type EpisodeEditImpactWarnings,
  parseEpisodeEditImpactWarnings,
} from '../../utils/episodeEditImpact';
import {
  type EpisodeUnfinishedVisit,
  parseEpisodeUnfinishedVisits,
} from '../../utils/episodeUnfinishedVisits';
import { formatDateForDisplay } from '../../utils/dateUtils';
import { MAX_EPISODE_END_DATE_OFFSET } from '../../utils/episodeBoundaryVisits';
import type { EpisodeEditorCapabilities } from '../../utils/chartEditPermissions';
import { statusDisplayLabel } from '../../utils/episodeStatus';
import {
  isClosedEpisodeLocked,
  useCanAmendClosedEpisode,
} from '../../utils/closedEpisodeLock';
import { DateField } from '../FormFields';
import { STATUS_DISPLAY_LABELS } from '../task/taskStatus';
import { EpisodeEditImpactList } from './EpisodeEditImpactList';
import EpisodeNotes from './EpisodeNotes';
import PreClaimReviewCard from './PreClaimReviewCard';

export type EpisodeEditorProps = {
  episodeId: string;
  capabilities?: EpisodeEditorCapabilities;
  /** Holds `episode:reopen`: may mark a completed episode incomplete, and a
   * reopened one completed again.*/
  canReopen?: boolean;
  onCancel?: () => void;
  onSaved?: () => void;
};

const FULL_EDITOR_CAPABILITIES: EpisodeEditorCapabilities = {
  canEditDates: true,
  canEditStatus: true,
  canEditPreClaimReview: true,
  canEditNotes: true,
};

const PCR_LENGTH = 14;

const MAX_REASON_LENGTH = 500;

type StatusAction = 'reopen' | 'complete';

const STATUS_ACTION_COPY: Record<
  StatusAction,
  { title: string; body: string; confirm: string; success: string }
> = {
  reopen: {
    title: 'Mark episode incomplete?',
    body: 'Visits in this episode become editable again.',
    confirm: 'Mark incomplete',
    success: 'The episode is incomplete and can be corrected.',
  },
  complete: {
    title: 'Mark episode completed?',
    body: 'Visits in this episode are locked again once its end date has passed.',
    confirm: 'Mark completed',
    success: 'The episode is completed.',
  },
};

const PCR_FIELDS = ['pcr_number_1', 'pcr_number_2', 'pcr_comment'] as const;

// The API refuses a status change sent together with any other field. When the
// user edits PCR fields and also changes the status, save the PCR fields first,
// then the status, so neither edit is lost.
const splitStatusFromPcr = (body: EpisodeUpdate): EpisodeUpdate[] => {
  if (body.status === undefined) {
    return [body];
  }
  const pcrBody: EpisodeUpdate = {};
  for (const field of PCR_FIELDS) {
    if (field in body) {
      pcrBody[field] = body[field];
    }
  }
  if (Object.keys(pcrBody).length === 0) {
    return [body];
  }
  const rest: EpisodeUpdate = { ...body };
  for (const field of PCR_FIELDS) {
    delete rest[field];
  }
  return [pcrBody, rest];
};

const isPcrValid = (value: string): boolean =>
  value.length === 0 || value.length === PCR_LENGTH;

// Statuses whose dates + active/inactive toggle are editable from the pencil.
const isCoreEditableStatus = (status: string): boolean =>
  status === 'active' || status === 'cancelled';

// Whole days for an ISO "YYYY-MM-DD" string, computed in UTC so it is timezone-safe.
const isoDayNumber = (iso: string): number => {
  const [y, m, d] = iso.split('-').map(Number);
  return Date.UTC(y, m - 1, d) / 86_400_000;
};

const episodeToForm = (episode: EpisodeResponse) => ({
  pcrNumber1: episode.pcr_number_1 ?? '',
  pcrNumber2: episode.pcr_number_2 ?? '',
  pcrComment: episode.pcr_comment ?? '',
  startDate: episode.start_date,
  endDate: episode.end_date,
  status: episode.status,
});

const EpisodeEditor: React.FC<EpisodeEditorProps> = ({
  episodeId,
  capabilities = FULL_EDITOR_CAPABILITIES,
  canReopen = false,
  onCancel,
  onSaved,
}) => {
  const { success: showSuccess, error: showError } = useToast();
  const canAmendClosedEpisode = useCanAmendClosedEpisode();
  const [episode, setEpisode] = useState<EpisodeResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [pcrNumber1, setPcrNumber1] = useState('');
  const [pcrNumber2, setPcrNumber2] = useState('');
  const [pcrComment, setPcrComment] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [statusDraft, setStatusDraft] = useState('');
  const [pendingImpact, setPendingImpact] =
    useState<EpisodeEditImpactWarnings | null>(null);
  const [pendingAction, setPendingAction] = useState<StatusAction | null>(null);
  const [actionReason, setActionReason] = useState('');
  const [unfinishedVisits, setUnfinishedVisits] = useState<
    EpisodeUnfinishedVisit[] | null
  >(null);

  const usingRcd = episode?.clinic?.using_rcd ?? false;
  const coreStatusEditable = episode
    ? isCoreEditableStatus(episode.status)
    : false;
  const datesEditable = capabilities.canEditDates && coreStatusEditable;
  const statusEditable = capabilities.canEditStatus && coreStatusEditable;
  // Complete is stored as status "completed", separate from Active/Inactive.
  // Reopening returns it to active and is not the Reactivate control, which only
  // applies to an inactive (cancelled) episode. Only an episode that was reopened
  // can be marked completed again.
  const canMarkIncomplete = episode?.status === 'completed' && canReopen;
  const canMarkCompleted =
    episode?.status === 'active' && Boolean(episode.reopened_at) && canReopen;
  // visits/charts stay editable on completed while the cert window
  // is open (or the actor holds amend-closed). Past end date, mark incomplete
  // is still how non-holders unlock.
  const completedVisitsStillEditable =
    episode?.status === 'completed' &&
    !isClosedEpisodeLocked(episode.is_closed, canAmendClosedEpisode);

  const resetForm = useCallback((data: EpisodeResponse) => {
    const form = episodeToForm(data);
    setPcrNumber1(form.pcrNumber1);
    setPcrNumber2(form.pcrNumber2);
    setPcrComment(form.pcrComment);
    setStartDate(form.startDate);
    setEndDate(form.endDate);
    setStatusDraft(form.status);
  }, []);

  const fetchEpisode = useCallback(async () => {
    setLoading(true);
    // Drop any stale impact confirmation from a previously loaded episode.
    setPendingImpact(null);
    try {
      const response = await getEpisode({
        path: { episode_id: episodeId },
      });
      const data = response.data ?? null;
      setEpisode(data);
      if (data) {
        resetForm(data);
      }
    } catch (error) {
      console.error('Failed to load episode:', error);
      showError(
        'Load Failed',
        'Unable to load episode data. Please try again.'
      );
      setEpisode(null);
    } finally {
      setLoading(false);
    }
  }, [episodeId, resetForm, showError]);

  useEffect(() => {
    void fetchEpisode();
  }, [fetchEpisode]);

  // Rereads the episode without the loading spinner, which would unmount the
  // notes and drop a draft. Only called when the form has no unsaved edits.
  const refreshEpisode = useCallback(async () => {
    try {
      const response = await getEpisode({ path: { episode_id: episodeId } });
      if (response.data) {
        setEpisode(response.data);
        resetForm(response.data);
      }
    } catch (error) {
      console.error('Failed to refresh episode:', error);
    }
  }, [episodeId, resetForm]);

  // Inline date error mirroring the backend rules: end must be after start, and the
  // range may not be lengthened beyond 60 days (a legacy range already over 60 days
  // may shrink or shift without tripping the cap).
  const dateError = useMemo(() => {
    if (!datesEditable || !episode) {
      return null;
    }
    if (endDate <= startDate) {
      return 'End date must be after start date.';
    }
    const newLength = isoDayNumber(endDate) - isoDayNumber(startDate);
    const oldLength =
      isoDayNumber(episode.end_date) - isoDayNumber(episode.start_date);
    if (newLength > MAX_EPISODE_END_DATE_OFFSET && newLength > oldLength) {
      return 'Episode cannot be longer than 60 days.';
    }
    return null;
  }, [datesEditable, episode, endDate, startDate]);

  const datesValid = dateError === null;

  const hasChanges = useMemo(() => {
    if (!episode) {
      return false;
    }
    const datesChanged =
      datesEditable &&
      (startDate !== episode.start_date || endDate !== episode.end_date);
    const statusChanged = statusEditable && statusDraft !== episode.status;
    const pcrChanged =
      capabilities.canEditPreClaimReview &&
      usingRcd &&
      ((episode.pcr_number_1 ?? '') !== pcrNumber1 ||
        (episode.pcr_number_2 ?? '') !== pcrNumber2 ||
        (episode.pcr_comment ?? '') !== pcrComment.trim());
    return datesChanged || statusChanged || pcrChanged;
  }, [
    episode,
    datesEditable,
    startDate,
    endDate,
    statusEditable,
    statusDraft,
    capabilities.canEditPreClaimReview,
    usingRcd,
    pcrNumber1,
    pcrNumber2,
    pcrComment,
  ]);

  const saveDisabled = useMemo(
    () =>
      saving ||
      !hasChanges ||
      !datesValid ||
      (capabilities.canEditPreClaimReview &&
        usingRcd &&
        (!isPcrValid(pcrNumber1) || !isPcrValid(pcrNumber2))),
    [
      saving,
      hasChanges,
      datesValid,
      capabilities.canEditPreClaimReview,
      usingRcd,
      pcrNumber1,
      pcrNumber2,
    ]
  );

  const buildBody = useCallback((): EpisodeUpdate => {
    if (!episode) {
      return {};
    }
    const body: EpisodeUpdate = {};
    if (capabilities.canEditPreClaimReview && usingRcd) {
      // Only send PCR fields that actually changed. Sending unchanged PCR fields
      // would trip the backend RCD-eligibility check for a dates/status-only edit
      // if the clinic's RCD flag changed after the form loaded.
      const p1 = pcrNumber1.length > 0 ? pcrNumber1 : null;
      const p2 = pcrNumber2.length > 0 ? pcrNumber2 : null;
      const comment = pcrComment.trim().length > 0 ? pcrComment.trim() : null;
      if (p1 !== (episode.pcr_number_1 ?? null)) {
        body.pcr_number_1 = p1;
      }
      if (p2 !== (episode.pcr_number_2 ?? null)) {
        body.pcr_number_2 = p2;
      }
      if (comment !== (episode.pcr_comment ?? null)) {
        body.pcr_comment = comment;
      }
    }
    if (datesEditable) {
      if (startDate !== episode.start_date) {
        body.start_date = startDate;
      }
      if (endDate !== episode.end_date) {
        body.end_date = endDate;
      }
    }
    if (statusEditable && statusDraft !== episode.status) {
      body.status = statusDraft;
    }
    return body;
  }, [
    episode,
    capabilities.canEditPreClaimReview,
    usingRcd,
    pcrNumber1,
    pcrNumber2,
    pcrComment,
    datesEditable,
    startDate,
    endDate,
    statusEditable,
    statusDraft,
  ]);

  const submit = useCallback(
    async (acknowledgeWarnings: boolean) => {
      setSaving(true);
      try {
        let saved: EpisodeResponse | undefined;
        for (const body of splitStatusFromPcr(buildBody())) {
          const response = await updateEpisode({
            path: { episode_id: episodeId },
            body,
            query: acknowledgeWarnings
              ? { acknowledge_warnings: true }
              : undefined,
            // The generated client resolves (does not throw) on a 4xx by default, so
            // opt into throwing to route 409 impact / 400 block responses through the
            // catch below instead of being mistaken for a successful save.
            throwOnError: true,
          });
          if (response.data) {
            saved = response.data;
            // Keep the drafts: if a later request fails, a retry sends only
            // what has not saved yet.
            setEpisode(response.data);
          }
        }
        if (saved) {
          resetForm(saved);
        }
        setPendingImpact(null);
        showSuccess('Episode Saved', 'Episode information was updated.');
        onSaved?.();
      } catch (error) {
        const impact = parseEpisodeEditImpactWarnings(error);
        if (impact) {
          setPendingImpact(impact);
          return;
        }
        console.error('Failed to save episode:', error);
        // Leave the confirmation screen so the error toast is the only prompt; the
        // user can re-open the editor to retry.
        setPendingImpact(null);
        showError(
          'Save Failed',
          getBusinessErrorMessage(error) ??
            'Unable to save episode information. Please try again.'
        );
      } finally {
        setSaving(false);
      }
    },
    [episodeId, buildBody, resetForm, showSuccess, showError, onSaved]
  );

  const startStatusAction = (action: StatusAction) => {
    setActionReason('');
    setUnfinishedVisits(null);
    setPendingAction(action);
  };

  const cancelStatusAction = () => {
    setPendingAction(null);
    setUnfinishedVisits(null);
  };

  // Reopen and complete are immediate actions, not part of Save: they go to
  // their own routes, which carry the permission check and the audit reason.
  const runStatusAction = useCallback(
    async (action: StatusAction, acknowledgeUnfinished: boolean) => {
      setSaving(true);
      const reason = actionReason.trim() || null;
      try {
        const response =
          action === 'reopen'
            ? await reopenEpisode({
                path: { episode_id: episodeId },
                body: { reason },
                throwOnError: true,
              })
            : await completeEpisode({
                path: { episode_id: episodeId },
                body: { reason },
                query: acknowledgeUnfinished
                  ? { acknowledge_unfinished: true }
                  : undefined,
                throwOnError: true,
              });
        setEpisode(response.data);
        resetForm(response.data);
        setPendingAction(null);
        setUnfinishedVisits(null);
        showSuccess('Episode Saved', STATUS_ACTION_COPY[action].success);
        onSaved?.();
      } catch (error) {
        const unfinished = parseEpisodeUnfinishedVisits(error);
        if (unfinished) {
          setUnfinishedVisits(unfinished);
          return;
        }
        console.error(`Failed to ${action} episode:`, error);
        setPendingAction(null);
        setUnfinishedVisits(null);
        // A refusal usually means the status moved elsewhere; show the current
        // one so the action is not offered again on a stale status.
        void refreshEpisode();
        showError(
          'Save Failed',
          getBusinessErrorMessage(error) ??
            (action === 'reopen'
              ? 'Unable to reopen episode. Please try again.'
              : 'Unable to complete episode. Please try again.')
        );
      } finally {
        setSaving(false);
      }
    },
    [
      actionReason,
      episodeId,
      resetForm,
      refreshEpisode,
      showSuccess,
      showError,
      onSaved,
    ]
  );

  const handleCancel = () => {
    if (episode) {
      resetForm(episode);
    }
    onCancel?.();
  };

  const toggleStatus = () => {
    setStatusDraft(prev => (prev === 'active' ? 'cancelled' : 'active'));
  };

  if (loading) {
    return (
      <div className='flex items-center justify-center py-8'>
        <Loader2 className='h-6 w-6 animate-spin text-muted-foreground' />
      </div>
    );
  }

  if (!episode) {
    return (
      <p className='text-sm text-muted-foreground'>
        Episode data is not available.
      </p>
    );
  }

  const statusActionCopy = pendingAction
    ? STATUS_ACTION_COPY[pendingAction]
    : null;
  const statusActionView = pendingAction && statusActionCopy && (
    <>
      <div className='space-y-3' data-testid='episode-status-action'>
        <p className='text-sm font-medium'>{statusActionCopy.title}</p>
        <p className='text-sm text-muted-foreground'>{statusActionCopy.body}</p>
        {unfinishedVisits ? (
          <div className='space-y-2' data-testid='episode-unfinished-visits'>
            <p className='text-sm'>
              These visits are not finished yet. Complete the episode anyway?
            </p>
            <ul className='max-h-48 space-y-1 overflow-y-auto rounded-md border px-3 py-2 text-sm'>
              {unfinishedVisits.map(visit => (
                <li key={visit.task_id} className='flex justify-between gap-3'>
                  <span>
                    {visit.label}
                    {visit.visit_date
                      ? ` · ${formatDateForDisplay(visit.visit_date)}`
                      : ''}
                  </span>
                  <span className='whitespace-nowrap text-muted-foreground'>
                    {STATUS_DISPLAY_LABELS[visit.status] ?? visit.status}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <div className='space-y-1'>
            <label
              htmlFor='episode-status-action-reason'
              className='text-sm font-medium'
            >
              Reason (optional)
            </label>
            <Textarea
              id='episode-status-action-reason'
              data-testid='episode-status-action-reason'
              value={actionReason}
              onChange={event => setActionReason(event.target.value)}
              maxLength={MAX_REASON_LENGTH}
              textareaSize='sm'
              rows={3}
              disabled={saving}
            />
          </div>
        )}
      </div>
      <DialogFooter>
        <Button
          type='button'
          variant='outline'
          onClick={cancelStatusAction}
          disabled={saving}
        >
          Cancel
        </Button>
        <Button
          type='button'
          data-testid='episode-status-action-confirm'
          onClick={() =>
            void runStatusAction(pendingAction, unfinishedVisits !== null)
          }
          disabled={saving}
        >
          {saving
            ? 'Saving…'
            : unfinishedVisits
              ? 'Complete anyway'
              : statusActionCopy.confirm}
        </Button>
      </DialogFooter>
    </>
  );

  if (pendingImpact) {
    return (
      <>
        <div className='space-y-3' data-testid='episode-edit-impact'>
          <p className='text-sm font-medium'>
            This edit affects existing records
          </p>
          <EpisodeEditImpactList warnings={pendingImpact} />
          <p className='text-sm text-muted-foreground'>
            These records are not modified. Do you want to proceed?
          </p>
        </div>
        <DialogFooter>
          <Button
            type='button'
            variant='outline'
            onClick={() => setPendingImpact(null)}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type='button'
            onClick={() => void submit(true)}
            disabled={saving}
          >
            {saving ? 'Saving…' : 'Confirm'}
          </Button>
        </DialogFooter>
      </>
    );
  }

  return (
    <>
      {statusActionView}
      {/* Hidden, not unmounted, under a status confirmation, so an unsaved
          episode note survives Cancel. */}
      <div className='space-y-4' hidden={pendingAction !== null}>
        {episode.clinic && (
          <div className='rounded-md border bg-muted/30 px-3 py-2 text-sm'>
            <p className='font-medium'>{episode.clinic.name}</p>
            {episode.clinic.clinic_key && (
              <p className='text-muted-foreground'>
                Clinic key: {episode.clinic.clinic_key}
              </p>
            )}
            {(episode.clinic.npi || episode.clinic.phone) && (
              <p className='text-muted-foreground'>
                {[
                  episode.clinic.npi && `NPI ${episode.clinic.npi}`,
                  episode.clinic.phone,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </p>
            )}
          </div>
        )}

        {episode.status === 'completed' && (
          <Alert
            className='border-amber-200 bg-amber-50'
            data-testid='episode-completed-banner'
          >
            <AlertDescription className='flex gap-2 text-amber-900'>
              <AlertTriangle className='mt-0.5 h-4 w-4 shrink-0 text-amber-600' />
              {completedVisitsStillEditable
                ? canReopen
                  ? 'This episode is completed. You can still edit visits and charts. Mark incomplete only if you need to change episode dates or status.'
                  : 'This episode is completed. You can still edit visits and charts. Ask a supervisor to mark it incomplete if you need to change episode dates or status.'
                : canReopen
                  ? 'This episode is completed and should not be edited. If you need to make corrections, please mark incomplete first.'
                  : 'This episode is completed. Ask a supervisor to mark it incomplete if corrections are needed.'}
            </AlertDescription>
          </Alert>
        )}

        <div className='space-y-3 rounded-md border px-3 py-3'>
          <p className='text-sm font-medium'>Episode</p>
          {datesEditable ? (
            <>
              <DateField
                label='Start date'
                htmlFor='episode-start-date'
                data-testid='episode-start-date'
                value={startDate}
                onChange={(value: string | null) => {
                  if (value) setStartDate(value);
                }}
                fieldSize='default'
              />
              <DateField
                label='End date'
                htmlFor='episode-end-date'
                data-testid='episode-end-date'
                value={endDate}
                onChange={(value: string | null) => {
                  if (value) setEndDate(value);
                }}
                fieldSize='default'
              />
              {dateError && (
                <p className='text-sm text-destructive'>{dateError}</p>
              )}
            </>
          ) : (
            <div className='text-sm text-muted-foreground'>
              <p>Start date: {formatDateForDisplay(episode.start_date)}</p>
              <p>End date: {formatDateForDisplay(episode.end_date)}</p>
            </div>
          )}

          <div className='flex items-center justify-between gap-3'>
            <span className='text-sm'>
              Status:{' '}
              <span data-testid='episode-status-value' className='font-medium'>
                {statusDisplayLabel(
                  statusEditable ? statusDraft : episode.status
                )}
              </span>
            </span>
            {statusEditable && (
              <Button
                type='button'
                variant='outline'
                size='sm'
                data-testid='episode-status-toggle'
                onClick={toggleStatus}
                disabled={saving}
              >
                {statusDraft === 'active'
                  ? 'Deactivate episode'
                  : 'Reactivate episode'}
              </Button>
            )}
            {(canMarkIncomplete || canMarkCompleted) && (
              <Button
                type='button'
                variant='outline'
                size='sm'
                data-testid={
                  canMarkIncomplete
                    ? 'episode-mark-incomplete'
                    : 'episode-mark-completed'
                }
                onClick={() =>
                  startStatusAction(canMarkIncomplete ? 'reopen' : 'complete')
                }
                disabled={saving || hasChanges}
              >
                {canMarkIncomplete ? 'Mark incomplete' : 'Mark completed'}
              </Button>
            )}
          </div>
          {(canMarkIncomplete || canMarkCompleted) && hasChanges && (
            <p
              className='text-xs text-muted-foreground'
              data-testid='episode-status-action-hint'
            >
              Save or discard your changes first.
            </p>
          )}
        </div>

        {capabilities.canEditPreClaimReview &&
          (usingRcd ? (
            <PreClaimReviewCard
              pcrNumber1={pcrNumber1}
              pcrNumber2={pcrNumber2}
              pcrComment={pcrComment}
              saving={saving}
              onPcrNumber1Change={setPcrNumber1}
              onPcrNumber2Change={setPcrNumber2}
              onPcrCommentChange={setPcrComment}
            />
          ) : (
            <p className='text-sm text-muted-foreground'>
              Pre-Claim Review is not enabled for this clinic.
            </p>
          ))}

        {capabilities.canEditNotes && (
          <EpisodeNotes
            episodeId={episodeId}
            className='border-t pt-4'
            description='Shared by every task in this episode.'
          />
        )}
      </div>
      {!pendingAction && (
        <DialogFooter>
          <Button
            type='button'
            variant='outline'
            onClick={handleCancel}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            type='button'
            onClick={() => void submit(false)}
            disabled={saveDisabled}
          >
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      )}
    </>
  );
};

export default EpisodeEditor;
